// legal-tabs.js — UI handlers for the Legal module (restored multi-tab HOME).
//
// All data comes from the server-side connector PROXY (legal-api.callConnector),
// which returns either { ok:true, result } or { ok:false, reason[, note] }. No tab
// here ever holds or sends a third-party API key. Where the shared free pool is
// spent (quota-exhausted), the tab offers a BYOK affordance that POSTs the user's
// own free key to /api/worklog/datasource/mykeys (server-side, encrypted) and
// re-runs. Trademark is the honest gap: no free keyed trademark search exists, so
// uspto.trademarks always returns reason:"unsupported" and we surface that.
import { getLicense } from './auth.js';
import { usageLog } from './api.js';
import * as legalApi from './legal-api.js';

const _MODULE = 'Legal';

// BYOK provider that backs each tab whose connector can run on a user key.
// (patents/CFR/bills/regs map to uspto/apidatagov; case law -> courtlistener.)
const TAB_BYOK_PROVIDER = {
  art: 'uspto',
  compliance: 'apidatagov',
  itar: 'apidatagov',
  congress: 'apidatagov',
  caselaw: 'courtlistener'
};

// ==========================================================================
// Status helper — sets #l-status-{tabId} textContent
// ==========================================================================
function status(tab, msg) {
  const el = document.getElementById(`l-status-${tab}`);
  if (el) el.textContent = msg;
}

// ==========================================================================
// Tab switching
// ==========================================================================
function switchLegalTab(targetId) {
  document.querySelectorAll('.ltab').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.lt === targetId);
  });
  document.querySelectorAll('.legal-panel').forEach(panel => {
    panel.style.display = panel.id === 'lp-' + targetId ? '' : 'none';
  });
}

// ==========================================================================
// Pro gate check
// ==========================================================================
function checkAccess() {
  const lic = getLicense();
  if (!lic) return false;
  const tier = (lic.tier || '').toLowerCase();
  if (!lic.isActive) return false;
  return tier === 'pro' || tier === 'research' || tier === 'admin' || tier === 'enterprise';
}

function renderGate(containerId) {
  const el = document.getElementById(containerId);
  if (!el) return;
  el.innerHTML = `
    <div style="text-align:center;padding:40px 20px;color:#94a3b8;">
      <div style="font-size:32px;margin-bottom:12px;"><i class="fas fa-lock"></i></div>
      <div style="color:#e2e8f0;font-size:15px;font-weight:600;margin-bottom:8px;">Pro License Required</div>
      <div style="font-size:13px;max-width:400px;margin:0 auto 20px;">
        The Legal module requires an active Pro or Enterprise license. Upgrade to access patent prior art search,
        regulatory compliance screening, ITAR/EAR classification, and expert report generation.
      </div>
      <button onclick="window.showPricing && window.showPricing()"
        style="background:#4fc3f7;color:#0d1117;border:none;padding:8px 22px;border-radius:5px;font-size:13px;font-weight:600;cursor:pointer;">
        View Pricing
      </button>
    </div>`;
}

// ==========================================================================
// HTML escape utility
// ==========================================================================
function esc(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// ==========================================================================
// Proxy-reason renderer — turns a { ok:false, reason, note } envelope into an
// honest, source-appropriate panel. For quota-exhausted on a BYOK-capable tab,
// renders an "add your own free key" form that POSTs to mykeys then re-runs.
// `rerun` is an optional callback to retry the search after a key is saved.
// ==========================================================================
function renderReason(containerId, envelope, tabId, rerun) {
  const el = document.getElementById(containerId);
  if (!el) return;
  const reason = envelope?.reason || 'error';
  const note = envelope?.note ? esc(envelope.note) : '';
  const provider = TAB_BYOK_PROVIDER[tabId];

  const box = (color, icon, title, msg, extra = '') => `
    <div style="border:1px solid ${color}44;border-radius:6px;padding:14px;background:#0d1117;margin-bottom:10px;">
      <div style="color:${color};font-weight:600;font-size:13px;margin-bottom:4px;"><i class="fas ${icon}"></i> ${esc(title)}</div>
      <div style="color:#94a3b8;font-size:12px;">${msg}${note ? ` <span style="color:#475569;">(${note})</span>` : ''}</div>
      ${extra}
    </div>`;

  switch (reason) {
    case 'not-configured':
      el.innerHTML = box('#fbbf24', 'fa-plug-circle-xmark', 'Source not configured',
        'This source needs a VaultSync key set server-side. It has not been provisioned yet for this deployment - contact your administrator.');
      break;

    case 'rate-limited':
      el.innerHTML = box('#fbbf24', 'fa-hourglass-half', 'Rate limited (free tier)',
        'The shared free tier for this source is briefly rate-limited. Wait a moment and retry'
        + (provider ? ', or add your own free key below to lift the shared cap.' : '.'),
        provider ? byokForm(provider, tabId, rerun) : '');
      break;

    case 'quota-exhausted':
      el.innerHTML = box('#f87171', 'fa-battery-empty', 'Shared free quota spent',
        'The shared free request pool for this source is used up for now.'
        + (provider ? ' Add your own free key below to keep searching - it is stored server-side, encrypted, and never leaves the server.' : ' Try again later.'),
        provider ? byokForm(provider, tabId, rerun) : '');
      break;

    case 'unsupported':
      el.innerHTML = box('#94a3b8', 'fa-circle-info', 'Not available',
        note || 'There is no free, keyless data source for this search. This is an honest gap, not an error.');
      break;

    case 'error':
    default:
      el.innerHTML = box('#f87171', 'fa-triangle-exclamation', 'Search failed',
        'The request could not be completed. Try again; if it persists the source may be temporarily unavailable.');
      break;
  }
}

// BYOK inline form for quota/rate cases. provider in {uspto,apidatagov,courtlistener}.
function byokForm(provider, tabId, rerun) {
  const label = {
    uspto: 'USPTO Open Data Portal key (free at data.uspto.gov/myodp)',
    apidatagov: 'api.data.gov key (free, instant, at api.data.gov)',
    courtlistener: 'CourtListener token (free at courtlistener.com)'
  }[provider] || 'API key';
  const signup = {
    uspto: 'https://data.uspto.gov/myodp',
    apidatagov: 'https://api.data.gov/signup/',
    courtlistener: 'https://www.courtlistener.com/help/api/rest/'
  }[provider] || '';
  const inputId = `byok-${tabId}-input`;
  const btnId = `byok-${tabId}-btn`;
  setTimeout(() => {
    const btn = document.getElementById(btnId);
    if (!btn) return;
    btn.addEventListener('click', async () => {
      const inp = document.getElementById(inputId);
      const key = (inp?.value || '').trim();
      if (!key) return;
      btn.disabled = true; btn.textContent = 'Saving…';
      const r = await legalApi.setMyKey(provider, key);
      if (r.ok) {
        btn.textContent = 'Saved - retrying…';
        if (typeof rerun === 'function') rerun();
      } else {
        btn.disabled = false; btn.textContent = 'Save & retry';
      }
    });
  }, 0);
  return `
    <div style="margin-top:10px;display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
      <input id="${inputId}" type="text" autocomplete="off" placeholder="${esc(label)}"
        style="flex:1;min-width:240px;background:#0a0f1a;border:1px solid #1e293b;color:#e2e8f0;border-radius:4px;padding:6px 10px;font-size:12px;font-family:monospace;-webkit-text-security:disc;">
      <button id="${btnId}" type="button" class="btn" style="background:#1e3a5f;border:1px solid #3b82f6;color:#93c5fd;font-size:12px;padding:5px 14px;">Save &amp; retry</button>
    </div>
    <div style="color:#475569;font-size:10px;margin-top:6px;">Your key is sent once to VaultSync, stored encrypted server-side, and used only for your account. It is never returned to the browser.${signup ? ` <a href="${esc(signup)}" target="_blank" rel="noopener" style="color:#4fc3f7;">How to get a free key &rarr;</a>` : ''}</div>`;
}

// ==========================================================================
// Card renderers — adapted to the PROXY normalized field names
// ==========================================================================

// uspto.patents -> {title,patentNumber,inventor?,date,url}
function renderPatentCard(p) {
  const patNum = p.patentNumber || '';
  const url = p.url || (patNum
    ? `https://patents.google.com/patent/US${encodeURIComponent(patNum)}`
    : `https://ppubs.uspto.gov/pubwebapp/`);
  return `
<div style="border:1px solid #1e293b;border-radius:6px;padding:12px;margin-bottom:10px;background:#0d1117;">
  <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;">
    <span style="color:#4fc3f7;font-weight:600;font-size:13px;">${esc(p.title)}</span>
    <span style="color:#475569;font-size:11px;white-space:nowrap;">${esc(p.date || '')}</span>
  </div>
  ${p.inventor ? `<div style="color:#64748b;font-size:11px;margin:4px 0;">${esc(p.inventor)}</div>` : ''}
  <div style="margin-top:8px;">
    <a href="${esc(url)}" target="_blank" rel="noopener" style="color:#4fc3f7;font-size:11px;text-decoration:none;">View patent →</a>
    ${patNum ? `<span style="color:#334155;margin:0 6px;">|</span><span style="color:#475569;font-size:11px;font-family:monospace;">${esc(patNum)}</span>` : ''}
  </div>
</div>`.trim();
}

// ecfr.search -> {title,hierarchy,url}
function renderCfrCard(r) {
  const cite = r.hierarchy || '';
  const url = r.url || 'https://www.ecfr.gov/';
  return `
<div style="border:1px solid #1e293b;border-radius:6px;padding:12px;margin-bottom:10px;background:#0d1117;">
  <div style="color:#a78bfa;font-size:12px;font-weight:600;">${esc(cite)}
    <a href="${esc(url)}" target="_blank" rel="noopener" style="color:#4fc3f7;font-size:11px;margin-left:8px;text-decoration:none;">eCFR →</a>
  </div>
  <div style="color:#e2e8f0;font-size:13px;margin:4px 0;">${esc(r.title)}</div>
</div>`.trim();
}

// courtlistener.search -> {caseName,court,dateFiled,citation?,url}
function renderCaseCard(r) {
  const url = r.url || '#';
  return `
<div style="border:1px solid #1e293b;border-radius:6px;padding:12px;margin-bottom:10px;background:#0d1117;">
  <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;">
    <span style="color:#4fc3f7;font-weight:600;font-size:13px;">${esc(r.caseName)}</span>
    <span style="color:#475569;font-size:11px;white-space:nowrap;">${esc(r.dateFiled || '')}</span>
  </div>
  <div style="color:#64748b;font-size:11px;margin:4px 0;">
    ${r.court ? esc(r.court) + (r.citation ? ' &nbsp;·&nbsp; ' : '') : ''}
    ${r.citation ? esc(r.citation) : ''}
  </div>
  ${url !== '#' ? `<div style="margin-top:8px;"><a href="${esc(url)}" target="_blank" rel="noopener" style="color:#4fc3f7;font-size:11px;text-decoration:none;">CourtListener →</a></div>` : ''}
</div>`.trim();
}

// caselaw.scotus -> {id,caseName,citation,dateFiled,snippet,score}
function renderScotusCard(r) {
  return `
<div style="border:1px solid #7c3aed44;border-radius:6px;padding:12px;margin-bottom:10px;background:#0d1117;">
  <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;">
    <span style="color:#a78bfa;font-weight:600;font-size:13px;">${esc(r.caseName)}</span>
    <span style="color:#475569;font-size:11px;white-space:nowrap;">${esc(r.dateFiled || '')}</span>
  </div>
  <div style="color:#64748b;font-size:11px;margin:4px 0;">
    ${r.citation ? esc(r.citation) : ''}
    <span style="background:#7c3aed22;color:#a78bfa;font-size:10px;font-weight:700;padding:1px 5px;border-radius:3px;margin-left:6px;">SCOTUS - local index</span>
  </div>
  ${r.snippet ? `<div style="color:#94a3b8;font-size:12px;margin-top:4px;">${esc(r.snippet)}</div>` : ''}
</div>`.trim();
}

// federalregister.search -> {title,type,publication_date,html_url,agency}
function renderFedRegCard(d) {
  const typeColor = d.type === 'Rule' ? '#f87171'
    : d.type === 'Proposed Rule' ? '#fbbf24' : '#94a3b8';
  return `
<div style="border:1px solid #1e293b;border-radius:6px;padding:12px;margin-bottom:10px;background:#0d1117;">
  <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;">
    <span style="color:#4fc3f7;font-weight:600;font-size:13px;">${esc(d.title)}</span>
    <span style="color:${typeColor};font-size:11px;white-space:nowrap;">${esc(d.type || '')}</span>
  </div>
  <div style="color:#64748b;font-size:11px;margin:4px 0;">
    ${d.agency ? esc(d.agency) + ' &nbsp;·&nbsp; ' : ''}
    ${d.publication_date ? esc(d.publication_date) : ''}
  </div>
  ${d.html_url ? `<div style="margin-top:8px;"><a href="${esc(d.html_url)}" target="_blank" rel="noopener" style="color:#4fc3f7;font-size:11px;text-decoration:none;">Federal Register →</a></div>` : ''}
</div>`.trim();
}

// ofac.screen match -> {name,type,programs,score,sdnId}
function renderSanctionCard(r) {
  const programs = Array.isArray(r.programs) ? r.programs.join(', ') : (r.programs || '');
  const scorePct = typeof r.score === 'number' ? `${Math.round(r.score * (r.score <= 1 ? 100 : 1))}%` : (r.score != null ? esc(String(r.score)) : '');
  return `
<div style="border:1px solid #f8717144;border-radius:6px;padding:12px;margin-bottom:10px;background:#0d1117;">
  <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;">
    <span style="color:#f87171;font-weight:700;font-size:13px;">${esc(r.name)}</span>
    ${scorePct ? `<span style="background:#f8717122;color:#f87171;font-size:10px;font-weight:700;padding:1px 6px;border-radius:3px;white-space:nowrap;border:1px solid #f8717144;">match ${esc(scorePct)}</span>` : ''}
  </div>
  ${r.type ? `<div style="color:#64748b;font-size:11px;margin:2px 0;">${esc(r.type)}</div>` : ''}
  ${programs ? `<div style="color:#fbbf24;font-size:11px;margin:2px 0;">Programs: ${esc(programs)}</div>` : ''}
  ${r.sdnId ? `<div style="color:#475569;font-size:11px;font-family:monospace;margin-top:4px;">SDN ID: ${esc(String(r.sdnId))}</div>` : ''}
</div>`.trim();
}

// congress.bills -> {billNumber,congress,title,latestAction,url}
function renderBillCard(b) {
  const billRef = b.congress && b.billNumber ? `${esc(b.congress)} Cong. ${esc(b.billNumber)}` : '';
  return `
<div style="border:1px solid #1e293b;border-radius:6px;padding:12px;margin-bottom:10px;background:#0d1117;">
  <div style="color:#4fc3f7;font-weight:600;font-size:13px;">${esc(b.title)}</div>
  <div style="color:#64748b;font-size:11px;margin:4px 0;">${billRef}</div>
  ${b.latestAction ? `<div style="color:#94a3b8;font-size:12px;margin-top:4px;">${esc(b.latestAction)}</div>` : ''}
  ${b.url ? `<div style="margin-top:8px;"><a href="${esc(b.url)}" target="_blank" rel="noopener" style="color:#4fc3f7;font-size:11px;text-decoration:none;">Congress.gov →</a></div>` : ''}
</div>`.trim();
}

// ==========================================================================
// Generated document renderer
// ==========================================================================
function renderGeneratedDoc(containerId, result) {
  const el = document.getElementById(containerId);
  if (!el) return;
  el.style.display = '';
  el.innerHTML = result.document || '<p style="color:#64748b;">No document generated.</p>';
}

// Render a list of cards, or an empty-state line.
function renderList(containerId, items, renderFn, emptyMsg) {
  const el = document.getElementById(containerId);
  if (!el) return;
  if (!items || items.length === 0) {
    el.innerHTML = `<p style="color:#64748b;font-size:12px;padding:8px 0;">${esc(emptyMsg)}</p>`;
  } else {
    el.innerHTML = items.map(renderFn).join('');
  }
}

// ==========================================================================
// Tab 1: Prior Art -> uspto.patents
// ==========================================================================
function initArtTab() {
  const form = document.getElementById('f-art');
  if (!form) return;
  let lastPatents = [];

  async function run() {
    if (!checkAccess()) { renderGate('art-results'); return; }
    const desc = (form.elements['art_desc']?.value || '').trim();
    const domain = form.elements['art_domain']?.value || 'general';
    const limit = parseInt(form.elements['art_limit']?.value || '10', 10);
    if (!desc) { status('art', 'Please enter a technology description.'); return; }

    status('art', 'Searching USPTO patents…');
    const resultsEl = document.getElementById('art-results');
    if (resultsEl) resultsEl.innerHTML = '';
    const gen = document.getElementById('art-generated');
    if (gen) gen.innerHTML = '';

    const query = domain !== 'general' ? `${desc} ${domain.replace(/-/g, ' ')}` : desc;
    usageLog.run(_MODULE, 'patent-search');

    const env = await legalApi.searchPatents(query, limit);
    if (!env.ok) { renderReason('art-results', env, 'art', run); status('art', 'Search unavailable.'); return; }

    lastPatents = env.result?.results || [];
    renderList('art-results', lastPatents, renderPatentCard, 'No patents found. Try different terms or a broader domain.');
    if (lastPatents.length) {
      const btnWrap = document.getElementById('art-generate-btn');
      if (btnWrap) btnWrap.style.display = '';
    }
    status('art', `Found ${lastPatents.length} patent${lastPatents.length !== 1 ? 's' : ''}.`);
  }

  form.addEventListener('submit', e => { e.preventDefault(); run(); });

  const genBtn = document.getElementById('btn-art-generate');
  if (genBtn) {
    genBtn.addEventListener('click', async () => {
      if (!checkAccess()) { renderGate('art-generated'); return; }
      const desc = (form.elements['art_desc']?.value || '').trim();
      if (!desc && lastPatents.length === 0) { status('art', 'Run a patent search first.'); return; }
      status('art', 'Generating prior art analysis…');
      genBtn.disabled = true;
      usageLog.run(_MODULE, 'generate-prior-art');
      try {
        const result = await legalApi.generateLegalDocument('prior_art', desc, {
          patents: lastPatents,
          domain: form.elements['art_domain']?.value || 'general'
        });
        renderGeneratedDoc('art-generated', result);
        status('art', 'Prior art analysis generated.');
      } catch (err) {
        status('art', 'Generation failed: ' + err.message);
      } finally { genBtn.disabled = false; }
    });
  }
}

// ==========================================================================
// Tab 2: Compliance -> ecfr.search
// ==========================================================================
function initComplianceTab() {
  const form = document.getElementById('f-compliance');
  if (!form) return;
  let lastRegs = [];

  const domainTerms = {
    'nuclear-nrc': 'nuclear reactor facility license 10 CFR 50',
    'defense-mil': 'defense acquisition military specification DFARS 48 CFR',
    'aerospace-faa': 'airworthiness certification aviation 14 CFR FAA',
    'chemical-epa': 'hazardous waste emissions RCRA 40 CFR EPA',
    'materials-astm': 'materials testing standard specification 29 CFR',
    'safety-osha': 'workplace safety hazard 29 CFR OSHA'
  };

  async function run() {
    if (!checkAccess()) { renderGate('compliance-results'); return; }
    const desc = (form.elements['comp_desc']?.value || '').trim();
    const domain = form.elements['comp_domain']?.value || 'safety-osha';
    if (!desc) { status('compliance', 'Please describe the computed parameters.'); return; }

    status('compliance', 'Searching eCFR regulations…');
    const resultsEl = document.getElementById('compliance-results');
    if (resultsEl) resultsEl.innerHTML = '';
    const gen = document.getElementById('compliance-generated');
    if (gen) gen.innerHTML = '';

    const query = domainTerms[domain] || domain.replace(/-/g, ' ');
    usageLog.run(_MODULE, 'compliance-search');

    const env = await legalApi.searchCFR(query);
    if (!env.ok) { renderReason('compliance-results', env, 'compliance', run); status('compliance', 'Search unavailable.'); return; }

    lastRegs = env.result?.results || [];
    renderList('compliance-results', lastRegs, renderCfrCard, 'No regulatory provisions found. Try different terms.');
    if (lastRegs.length) {
      const btnWrap = document.getElementById('compliance-generate-btn');
      if (btnWrap) btnWrap.style.display = '';
    }
    status('compliance', `Found ${lastRegs.length} provision${lastRegs.length !== 1 ? 's' : ''}.`);
  }

  form.addEventListener('submit', e => { e.preventDefault(); run(); });

  const genBtn = document.getElementById('btn-compliance-generate');
  if (genBtn) {
    genBtn.addEventListener('click', async () => {
      if (!checkAccess()) { renderGate('compliance-generated'); return; }
      const desc = (form.elements['comp_desc']?.value || '').trim();
      const params = (form.elements['comp_params']?.value || '').trim();
      if (!desc && lastRegs.length === 0) { status('compliance', 'Run a regulation search first.'); return; }
      status('compliance', 'Generating compliance matrix…');
      genBtn.disabled = true;
      usageLog.run(_MODULE, 'generate-compliance');
      try {
        const result = await legalApi.generateLegalDocument('compliance', desc, {
          regulations: lastRegs,
          params,
          domain: form.elements['comp_domain']?.value || 'safety-osha'
        });
        renderGeneratedDoc('compliance-generated', result);
        status('compliance', 'Compliance matrix generated.');
      } catch (err) {
        status('compliance', 'Generation failed: ' + err.message);
      } finally { genBtn.disabled = false; }
    });
  }
}

// ==========================================================================
// Tab 3: ITAR/EAR -> ecfr.search (augmented with export-control terms)
// ==========================================================================
function initItarTab() {
  const form = document.getElementById('f-itar');
  if (!form) return;
  let lastCcl = [];

  async function run() {
    if (!checkAccess()) { renderGate('itar-results'); return; }
    const desc = (form.elements['itar_desc']?.value || '').trim();
    if (!desc) { status('itar', 'Please describe the technology.'); return; }

    status('itar', 'Screening against EAR/ITAR provisions…');
    const resultsEl = document.getElementById('itar-results');
    if (resultsEl) resultsEl.innerHTML = '';
    const gen = document.getElementById('itar-generated');
    if (gen) gen.innerHTML = '';

    usageLog.run(_MODULE, 'itar-screen');

    const query = `${desc} export control ITAR EAR CCL USML`;
    const env = await legalApi.searchCFR(query);
    if (!env.ok) { renderReason('itar-results', env, 'itar', run); status('itar', 'Screening unavailable.'); return; }

    lastCcl = env.result?.results || [];
    renderList('itar-results', lastCcl, renderCfrCard, 'No matching EAR/ITAR provisions found. Consult counsel for classification.');
    if (lastCcl.length) {
      const btnWrap = document.getElementById('itar-generate-btn');
      if (btnWrap) btnWrap.style.display = '';
    }
    status('itar', `Found ${lastCcl.length} provision${lastCcl.length !== 1 ? 's' : ''}.`);
  }

  form.addEventListener('submit', e => { e.preventDefault(); run(); });

  const genBtn = document.getElementById('btn-itar-generate');
  if (genBtn) {
    genBtn.addEventListener('click', async () => {
      if (!checkAccess()) { renderGate('itar-generated'); return; }
      const desc = (form.elements['itar_desc']?.value || '').trim();
      if (!desc && lastCcl.length === 0) { status('itar', 'Run a technology screening first.'); return; }
      status('itar', 'Generating classification memorandum…');
      genBtn.disabled = true;
      usageLog.run(_MODULE, 'generate-itar-memo');
      try {
        const result = await legalApi.generateLegalDocument('itar', desc, {
          cclResults: lastCcl,
          origin: form.elements['itar_origin']?.value || 'Unknown',
          destination: form.elements['itar_destination']?.value || 'general export',
          dualUse: form.elements['itar_dual_use']?.checked || false
        });
        renderGeneratedDoc('itar-generated', result);
        status('itar', 'Classification memorandum generated.');
      } catch (err) {
        status('itar', 'Generation failed: ' + err.message);
      } finally { genBtn.disabled = false; }
    });
  }
}

// ==========================================================================
// Tab 4: Expert Report (no external source — pure generation)
// ==========================================================================
function initReportTab() {
  const form = document.getElementById('f-report');
  if (!form) return;
  const outputEl = document.getElementById('report-output');

  form.addEventListener('submit', async e => {
    e.preventDefault();
    if (!checkAccess()) { renderGate('report-output'); return; }
    const reportType = form.elements['report_type']?.value || 'expert-witness-declaration';
    const context = (form.elements['report_context']?.value || '').trim();
    const reportTitle = (form.elements['report_title']?.value || '').trim() || 'Expert Report';
    const reportAuthor = (form.elements['report_author']?.value || '').trim();
    if (!context) { status('report', 'Please provide computation context or results.'); return; }

    status('report', 'Generating report…');
    if (outputEl) outputEl.innerHTML = '';
    const actionBar = document.getElementById('report-actions');
    if (actionBar) actionBar.style.display = 'none';
    usageLog.run(_MODULE, 'generate-expert-report');

    const reportTypeLabels = {
      'patent-technical-section': 'Patent Technical Section',
      'expert-witness-declaration': 'Expert Witness Declaration',
      'compliance-certification': 'Compliance Certification',
      'prior-art-analysis': 'Prior Art Analysis'
    };

    try {
      const result = await legalApi.generateLegalDocument('expert_report', context, {
        reportType: reportTypeLabels[reportType] || reportType,
        reportTitle,
        reportAuthor
      });
      if (outputEl) {
        outputEl.innerHTML = result.document || '<p style="color:#64748b;">No document generated.</p>';
        outputEl.style.display = '';
      }
      if (actionBar) actionBar.style.display = 'flex';
      status('report', 'Report generated.');
    } catch (err) {
      status('report', 'Generation failed: ' + err.message);
    }
  });

  const copyBtn = document.getElementById('btn-report-copy');
  if (copyBtn) {
    copyBtn.addEventListener('click', async () => {
      if (!outputEl) return;
      try {
        await navigator.clipboard.writeText(outputEl.innerText || outputEl.textContent || '');
        copyBtn.textContent = 'Copied!';
        setTimeout(() => { copyBtn.textContent = 'Copy to Clipboard'; }, 2000);
      } catch (_err) {
        const range = document.createRange();
        range.selectNodeContents(outputEl);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        document.execCommand('copy');
        sel.removeAllRanges();
        copyBtn.textContent = 'Copied!';
        setTimeout(() => { copyBtn.textContent = 'Copy to Clipboard'; }, 2000);
      }
    });
  }

  const dlBtn = document.getElementById('btn-report-download');
  if (dlBtn) {
    dlBtn.addEventListener('click', () => {
      if (!outputEl) return;
      const reportTitle = (document.getElementById('report_title')?.value || 'expert-report').trim();
      const safeTitle = reportTitle.replace(/[^a-z0-9_-]/gi, '_').toLowerCase() || 'report';
      const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>${esc(reportTitle)}</title>
<style>
  body { background:#0d1117; color:#e2e8f0; font-family:'Segoe UI',system-ui,sans-serif; margin:0; padding:32px; }
  @media print { body { background:#fff; color:#000; } a { color:#000; } }
</style>
</head>
<body>
${outputEl.innerHTML}
</body>
</html>`;
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeTitle}.html`;
      a.click();
      URL.revokeObjectURL(url);
    });
  }
}

// ==========================================================================
// Tab 5: Trademark -> uspto.trademarks (ALWAYS unsupported — honest gap)
// ==========================================================================
function initTrademarkTab() {
  const form = document.getElementById('f-trademark');
  if (!form) return;

  form.addEventListener('submit', async e => {
    e.preventDefault();
    if (!checkAccess()) { renderGate('trademark-results'); return; }
    const query = (form.elements['tm_query']?.value || '').trim();
    if (!query) { status('trademark', 'Enter a trademark term or owner name.'); return; }
    status('trademark', 'Checking trademark availability…');
    const resultsEl = document.getElementById('trademark-results');
    if (resultsEl) resultsEl.innerHTML = '';
    usageLog.run(_MODULE, 'trademark-search');

    const env = await legalApi.searchTrademarks(query);
    // Trademark always returns ok:false reason:"unsupported"; renderReason
    // surfaces the honest "no free trademark search" note from the proxy.
    if (!env.ok) { renderReason('trademark-results', env, 'trademark'); status('trademark', 'No free trademark search available.'); return; }

    // Defensive: if a future build does return results, render generically.
    const items = env.result?.results || [];
    renderList('trademark-results', items, r => `
<div style="border:1px solid #1e293b;border-radius:6px;padding:12px;margin-bottom:10px;background:#0d1117;">
  <span style="color:#4fc3f7;font-weight:600;font-size:13px;">${esc(r.title || r.mark || '')}</span>
</div>`.trim(), 'No trademarks found.');
    status('trademark', `Found ${items.length} mark${items.length !== 1 ? 's' : ''}.`);
  });
}

// ==========================================================================
// Tab 5b: RECAP / federal court filings -> courtlistener.search
// ==========================================================================
function initRecapTab() {
  const form = document.getElementById('f-recap');
  if (!form) return;

  async function run() {
    if (!checkAccess()) { renderGate('recap-results'); return; }
    const query = (form.elements['recap_query']?.value || '').trim();
    if (!query) { status('recap', 'Enter a case name, party, or keyword.'); return; }
    status('recap', 'Searching CourtListener…');
    const resultsEl = document.getElementById('recap-results');
    if (resultsEl) resultsEl.innerHTML = '';
    usageLog.run(_MODULE, 'recap-search');

    const env = await legalApi.searchCourtListener(query);
    if (!env.ok) { renderReason('recap-results', env, 'caselaw', run); status('recap', 'Search unavailable.'); return; }

    const items = env.result?.results || [];
    renderList('recap-results', items, renderCaseCard, 'No filings found. The document may exist in PACER but not be indexed by CourtListener.');
    status('recap', `Found ${items.length} result${items.length !== 1 ? 's' : ''}.`);
  }

  form.addEventListener('submit', e => { e.preventDefault(); run(); });
}

// ==========================================================================
// Tab 6: Case Law -> caselaw.scotus (local) + courtlistener.search
// ==========================================================================
function initCaseLawTab() {
  const form = document.getElementById('f-caselaw');
  if (!form) return;

  async function run() {
    if (!checkAccess()) { renderGate('caselaw-results'); return; }
    const query = (form.elements['cl_query']?.value || '').trim();
    if (!query) { status('caselaw', 'Enter a case name, citation, or keyword.'); return; }
    status('caselaw', 'Searching SCOTUS local index + CourtListener…');
    const el = document.getElementById('caselaw-results');
    if (el) el.innerHTML = '';
    usageLog.run(_MODULE, 'caselaw-search');

    // Run both: local SCOTUS index (always keyless) and CourtListener (proxy).
    const [scotusEnv, clEnv] = await Promise.all([
      legalApi.searchScotus(query, 20),
      legalApi.searchCourtListener(query)
    ]);

    let html = '';

    // SCOTUS local index — empty index returns count 0 + a "not yet loaded" note;
    // that's a STATE, not an error.
    if (scotusEnv.ok) {
      const sres = scotusEnv.result || {};
      const srows = sres.results || [];
      if (srows.length) {
        html += `<div style="color:#a78bfa;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;margin:4px 0 8px;">SCOTUS - local full-text index</div>`;
        html += srows.map(renderScotusCard).join('');
      } else if (sres.note) {
        html += `<div style="border:1px solid #7c3aed44;border-radius:6px;padding:10px 12px;background:#0d1117;margin-bottom:10px;color:#94a3b8;font-size:12px;">
          <i class="fas fa-circle-info" style="color:#a78bfa;"></i> SCOTUS local index: ${esc(sres.note)}</div>`;
      }
    }

    // CourtListener (federal + state appellate). 429 -> rate-limited reason.
    if (clEnv.ok) {
      const crows = clEnv.result?.results || [];
      if (crows.length) {
        html += `<div style="color:#4fc3f7;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;margin:14px 0 8px;">CourtListener</div>`;
        html += crows.map(renderCaseCard).join('');
      }
    } else {
      // Surface the CourtListener failure inline beneath any SCOTUS results.
      const tmp = document.createElement('div');
      tmp.id = 'caselaw-cl-reason';
      html += `<div id="caselaw-cl-reason" style="margin-top:14px;"></div>`;
    }

    if (el) el.innerHTML = html || '';

    // If CourtListener failed, render its reason panel (with BYOK retry) into the slot.
    if (!clEnv.ok) renderReason('caselaw-cl-reason', clEnv, 'caselaw', run);

    const total = (scotusEnv.ok ? (scotusEnv.result?.results || []).length : 0)
      + (clEnv.ok ? (clEnv.result?.results || []).length : 0);
    if (total === 0 && el && !el.innerHTML.trim()) {
      el.innerHTML = '<p style="color:#64748b;font-size:12px;padding:8px 0;">No cases found. Try different search terms.</p>';
    }
    status('caselaw', `Found ${total} result${total !== 1 ? 's' : ''}.`);
  }

  form.addEventListener('submit', e => { e.preventDefault(); run(); });
}

// ==========================================================================
// Tab 7: Federal Register -> federalregister.search
// ==========================================================================
function initFedRegisterTab() {
  const form = document.getElementById('f-fedregister');
  if (!form) return;

  async function run() {
    if (!checkAccess()) { renderGate('fedregister-results'); return; }
    const query = (form.elements['fr_query']?.value || '').trim();
    if (!query) { status('fedregister', 'Enter a keyword or agency name.'); return; }
    status('fedregister', 'Searching Federal Register…');
    const el = document.getElementById('fedregister-results');
    if (el) el.innerHTML = '';
    usageLog.run(_MODULE, 'fedregister-search');

    const env = await legalApi.searchFederalRegister(query, 20);
    if (!env.ok) { renderReason('fedregister-results', env, 'fedregister', run); status('fedregister', 'Search unavailable.'); return; }

    const items = env.result?.results || [];
    renderList('fedregister-results', items, renderFedRegCard, 'No documents found. Try different terms.');
    status('fedregister', `Found ${items.length} document${items.length !== 1 ? 's' : ''}.`);
  }

  form.addEventListener('submit', e => { e.preventDefault(); run(); });
}

// ==========================================================================
// Tab 8: Sanctions Screen -> ofac.screen
// ==========================================================================
function initSanctionsTab() {
  const form = document.getElementById('f-sanctions');
  if (!form) return;

  async function run() {
    if (!checkAccess()) { renderGate('sanctions-results'); return; }
    const query = (form.elements['sc_query']?.value || '').trim();
    if (!query) { status('sanctions', 'Enter an entity or individual name to screen.'); return; }
    status('sanctions', 'Screening against OFAC SDN…');
    const el = document.getElementById('sanctions-results');
    if (el) el.innerHTML = '';
    usageLog.run(_MODULE, 'sanctions-screen');

    const env = await legalApi.screenSanctions(query);
    if (!env.ok) { renderReason('sanctions-results', env, 'sanctions', run); status('sanctions', 'Screening unavailable.'); return; }

    const res = env.result || {};
    const matches = res.matches || [];
    const asOf = res.asOf ? esc(res.asOf) : '';

    if (!el) { status('sanctions', `Screened. ${matches.length} match${matches.length !== 1 ? 'es' : ''}.`); return; }

    if (matches.length === 0) {
      el.innerHTML = `
        <div style="border:1px solid #22c55e44;border-radius:6px;padding:16px;background:#0d1117;margin-bottom:10px;">
          <div style="color:#22c55e;font-weight:700;font-size:13px;margin-bottom:4px;"><i class="fas fa-check"></i> No Matches Found</div>
          <div style="color:#94a3b8;font-size:12px;">No OFAC SDN records found for <em>${esc(query)}</em>${asOf ? ` (list as of ${asOf})` : ''}. This is not a definitive compliance clearance - verify with your compliance officer against the official OFAC list.</div>
        </div>`;
    } else {
      el.innerHTML = `
        <div style="border:1px solid #f8717144;border-radius:6px;padding:12px;background:#0d1117;margin-bottom:12px;">
          <div style="color:#f87171;font-weight:700;font-size:13px;"><i class="fas fa-triangle-exclamation"></i> ${matches.length} Possible Match${matches.length !== 1 ? 'es' : ''} - Review Required</div>
          <div style="color:#94a3b8;font-size:11px;margin-top:4px;">A match is a LEAD, not a finding. False positives are common - verify identity before any action. ${asOf ? `OFAC list as of ${asOf}.` : ''}</div>
        </div>` + matches.map(renderSanctionCard).join('');
    }
    status('sanctions', `Screened. ${matches.length} match${matches.length !== 1 ? 'es' : ''}${asOf ? ` (as of ${asOf})` : ''}.`);
  }

  form.addEventListener('submit', e => { e.preventDefault(); run(); });
}

// ==========================================================================
// Tab 9: Congress -> congress.bills
// ==========================================================================
function initCongressTab() {
  const form = document.getElementById('f-congress');
  if (!form) return;

  async function run() {
    if (!checkAccess()) { renderGate('congress-results'); return; }
    const query = (form.elements['cg_query']?.value || '').trim();
    const limit = parseInt(form.elements['cg_limit']?.value || '10', 10);
    if (!query) { status('congress', 'Enter keywords to search legislation.'); return; }
    status('congress', 'Searching Congress.gov…');
    const el = document.getElementById('congress-results');
    if (el) el.innerHTML = '';
    usageLog.run(_MODULE, 'congress-search');

    const env = await legalApi.searchCongress(query, limit);
    if (!env.ok) { renderReason('congress-results', env, 'congress', run); status('congress', 'Search unavailable.'); return; }

    const items = env.result?.results || [];
    renderList('congress-results', items, renderBillCard, 'No bills found. Try different keywords.');
    status('congress', `Found ${items.length} bill${items.length !== 1 ? 's' : ''}.`);
  }

  form.addEventListener('submit', e => { e.preventDefault(); run(); });
}

// ==========================================================================
// Main init_ui export
// ==========================================================================
// "Tools" tab: launchers for the legal PLUGINS the data tabs do not cover
// (analysis / drafting / calculators). Opening one hands off to the plugin host
// (window.gdbsPlugins.openById -> the plugin's own view). Each is also bindable
// as a Worklog automation. Keyless or on our central key; no user key required.
const LEGAL_APPS = [
  { id: 'legal_analyst',    title: 'Legal Analyst',        icon: 'fa-gavel',          desc: 'Citations & Bluebook, clause detection, redline, cross-refs, risk - rules-based.' },
  { id: 'legal_deadlines',  title: 'Deadlines & SOL',      icon: 'fa-calendar-check', desc: 'Court-date / FRCP, statute of limitations, pre/post-judgment interest.' },
  { id: 'legal_drafting',   title: 'Drafting',             icon: 'fa-file-pen',       desc: 'Table of Authorities, FOIA, litigation hold, templates, readability.' },
  { id: 'legal_litigation', title: 'Litigation Utilities', icon: 'fa-folder-open',    desc: 'Redaction, privilege log, Bates numbering, brief word-limit, proof of service.' },
  { id: 'legal_regwatch',   title: 'Regulatory Watch',     icon: 'fa-bell',           desc: 'Watch a CFR part / agency for new Federal Register + eCFR activity.' },
  { id: 'legal_analytics',  title: 'Legal Analytics',      icon: 'fa-chart-line',     desc: 'Rulemaking / eCFR / EDGAR trends + citation analysis over your documents.' },
];
// ONE-TIME document-level delegation for the Tools cards. Survives re-renders,
// panel display toggles, and plugin-loader boot-order (the earlier per-card
// listeners were a late-bind hazard). Opens the plugin via the plugin host; if it
// is not yet registered, falls back to the Plugins catalog so the click is never
// a dead end.
let _legalAppsWired = false;
function installAppsDelegation() {
  if (_legalAppsWired || typeof document === 'undefined') return;
  _legalAppsWired = true;
  document.addEventListener('click', (e) => {
    const btn = e.target && e.target.closest ? e.target.closest('[data-legal-app]') : null;
    if (!btn) return;
    e.preventDefault();
    const id = btn.getAttribute('data-legal-app');
    if (!id) return;
    const gp = window.gdbsPlugins;
    if (gp && typeof gp.openById === 'function') {
      Promise.resolve(gp.openById('pro', id)).then(ok => {
        if (ok === false && typeof window.gdbsShowView === 'function') window.gdbsShowView('plugins');
      }).catch(() => { try { window.gdbsShowView('plugins'); } catch (_) {} });
    } else if (typeof window.gdbsShowView === 'function') {
      window.gdbsShowView('plugins');
    }
  });
}

function initAppsTab() {
  installAppsDelegation();
  const panel = document.getElementById('lp-apps');
  if (!panel) return;
  // Light surface (the Legal module renders light) - cards must match.
  const cards = LEGAL_APPS.map(a => `
    <button type="button" data-legal-app="${esc(a.id)}"
      style="text-align:left;display:flex;gap:12px;align-items:flex-start;padding:14px 16px;border:1px solid #e2e8f0;border-radius:10px;background:#ffffff;color:#1e293b;cursor:pointer;box-shadow:0 1px 2px rgba(15,23,42,0.04);">
      <i class="fas ${esc(a.icon)}" style="color:#7c3aed;font-size:18px;margin-top:2px;flex:0 0 auto;"></i>
      <span style="display:flex;flex-direction:column;gap:3px;min-width:0;">
        <span style="font-weight:600;font-size:14px;color:#1e293b;">${esc(a.title)}</span>
        <span style="font-size:12px;color:#64748b;line-height:1.4;">${esc(a.desc)}</span>
      </span>
    </button>`).join('');
  panel.innerHTML = `
    <p style="color:#475569;font-size:13px;margin:0 0 14px;max-width:760px;">Legal apps - analysis, drafting and calculators. They run in the browser (keyless) or on VaultSync's keys; no API key needed. Open one to use it; each is also bindable as a Worklog automation.</p>
    <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:12px;max-width:1100px;">${cards}</div>`;
}

export function init_ui() {
  // Sub-tab navigation
  document.querySelectorAll('.ltab').forEach(btn => {
    btn.addEventListener('click', () => {
      const target = btn.dataset.lt;
      if (target) switchLegalTab(target);
    });
  });

  // Activate first tab by default if none is active
  const firstTab = document.querySelector('.ltab');
  if (firstTab && !document.querySelector('.ltab.active')) {
    firstTab.classList.add('active');
    const firstPanelId = firstTab.dataset.lt;
    if (firstPanelId) {
      const firstPanel = document.getElementById('lp-' + firstPanelId);
      if (firstPanel) firstPanel.style.display = '';
    }
  }

  // Reuse existing ptab styling on the sub-tab buttons.
  document.querySelectorAll('.ltab').forEach(btn => btn.classList.add('ptab'));

  initArtTab();
  initComplianceTab();
  initItarTab();
  initReportTab();
  initTrademarkTab();
  initRecapTab();
  initCaseLawTab();
  initFedRegisterTab();
  initSanctionsTab();
  initCongressTab();
  initAppsTab();

  usageLog.module(_MODULE, 'view');
}
